<div class="container-fluid complyon-page workflows-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell workflows-shell mx-auto">
    <section class="workflows-header mb-3">
      <div class="d-flex flex-column flex-xl-row align-items-start align-items-xl-end justify-content-between gap-3">
        <div>
          <h1 class="complyon-title workflows-title mb-2 text-l">Workflows</h1>
          <p class="complyon-subtitle workflows-subtitle mb-0 text-s">
            Controlled business processes with policy rules, approval paths, evidence, risk and audit trails.
          </p>
        </div>

        <div class="d-flex align-items-center gap-3 ms-xl-auto">
          <button type="button" class="btn btn-link text-decoration-none workflows-reset-btn d-inline-flex align-items-center gap-2">
            <i class="bi bi-arrow-clockwise"></i>
            <span>Reset sample</span>
          </button>
          <button type="button" class="btn btn-primary-dark d-inline-flex align-items-center gap-2 px-3 py-2 rounded-3 fw-semibold" @click="open_start_workflow_modal">
            <i class="bi bi-plus-lg"></i>
            <span>Start workflow</span>
          </button>
        </div>
      </div>
    </section>

    <section class="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3 mb-3 workflows-toolbar">
      <div class="btn-group workflows-filter-group rounded-3 overflow-hidden" role="group" aria-label="Workflow view tabs">
        <button
          v-for="tab in workflow_tabs"
          :key="tab.id"
          type="button"
          :class="['workflows-filter-btn', { active: active_tab === tab.id }]"
          @click="set_tab(tab.id)"
        >
          <span>{{ tab.label }}</span>
        </button>
      </div>
    </section>

    <section v-if="active_tab === 'running'" class="workflow-running-layout">
      <div class="row g-3 mb-3">
        <div
          v-for="metric in running_metrics"
          :key="metric.label"
          class="col-12 col-md-6 col-xl-3"
        >
          <article class="workflow-metric-card rounded-4 p-3 h-100">
            <div class="workflow-metric-label mb-2">{{ metric.label }}</div>
            <div class="workflow-metric-value" :class="metric.tone ? `is-${metric.tone}` : ''">{{ metric.value }}</div>
            <div class="workflow-metric-subtitle">{{ metric.subtitle }}</div>
          </article>
        </div>
      </div>

      <section class="complyon-panel workflows-panel rounded-4 overflow-hidden">
        <div class="workflows-panel-head">
          <div class="workflows-panel-title">Running workflows</div>
          <div class="workflows-panel-subtitle">
            Every live process with its current stage, owner, risk and SLA. Open one for the governing rule, evidence, approvals, escalation and trail.
          </div>
        </div>
        <div class="table-responsive">
          <table class="table complyon-table workflows-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Ref</th>
                <th class="text-xs">Workflow</th>
                <th class="text-xs">Type</th>
                <th class="text-xs">Current stage</th>
                <th class="text-xs">Risk</th>
                <th class="text-xs">Owner</th>
                <th class="text-xs">Status</th>
                <th class="pe-3 text-xs"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="workflow in running_workflows"
                :key="workflow.id"
                :class="{ 'workflows-row-highlight': workflow.highlighted, 'table-row': workflow.drawer_key }"
                :role="workflow.drawer_key ? 'button' : null"
                :tabindex="workflow.drawer_key ? 0 : null"
                @click="open_workflow_drawer(workflow)"
                @keydown.enter="open_workflow_drawer(workflow)"
                @keydown.space.prevent="open_workflow_drawer(workflow)"
              >
                <td class="ps-3 workflows-ref">{{ workflow.ref }}</td>
                <td>
                  <div class="workflows-name">{{ workflow.name }}</div>
                </td>
                <td>
                  <div class="d-flex flex-column align-items-start gap-1">
                    <div class="workflows-type-label">{{ workflow.type_label }}</div>
                    <span v-if="workflow.type_badge" class="badge rounded-pill workflows-type-badge">{{ workflow.type_badge }}</span>
                  </div>
                </td>
                <td>
                  <div class="workflows-stage-cell">
                    <div class="workflows-stage-track">
                      <span class="workflows-stage-fill" :style="{ width: `${workflow.stage_progress || 0}%` }"></span>
                    </div>
                    <div class="workflows-stage-label">{{ workflow.current_stage }}</div>
                  </div>
                </td>
                <td>
                  <span class="badge complyon-badge workflows-pill text-xs" :class="workflow.risk.class">{{ workflow.risk.label }}</span>
                </td>
                <td>
                  <div class="workflows-owner">{{ workflow.owner || workflow.policy?.replace('POL-', '') || 'Owner' }}</div>
                </td>
                <td>
                  <span
                    class="badge complyon-badge workflows-pill text-xs"
                    :class="[workflow.status.class, { 'status-with-dot': workflow.status.label === 'Overdue' || workflow.status.label === 'Escalated' }]"
                  >
                    {{ workflow.status.label }}
                  </span>
                </td>
                <td class="pe-3 workflows-arrow">
                  <button
                    type="button"
                    class="btn btn-link text-decoration-none p-0 text-secondary"
                    @click.stop="open_workflow_drawer(workflow)"
                    aria-label="Open workflow details"
                  >
                    <i class="bi bi-chevron-right"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </section>

    <section v-else class="workflow-templates-layout">
      <div class="workflows-banner rounded-3 mb-3 d-flex align-items-start gap-2">
        <i class="bi bi-diagram-3"></i>
        <p class="mb-0">
          <strong>The process engine.</strong> Every module can trigger a workflow. Each template carries a risk level, a linked policy & control, required evidence and an escalation path. <strong>Supplier & Employee onboarding</strong> run as OnboardOS journeys; the rest orchestrate natively here.
        </p>
      </div>

      <div class="row g-3 workflows-template-grid">
        <div
          v-for="workflow in templates_workflows"
          :key="workflow.name"
          class="col-12 col-xl-6"
        >
          <article
            class="workflow-template-card rounded-4 p-3 h-100"
            :class="{ 'is-highlighted': workflow.highlighted }"
            role="button"
            tabindex="0"
            @click="open_workflow_drawer(workflow)"
            @keydown.enter="open_workflow_drawer(workflow)"
            @keydown.space.prevent="open_workflow_drawer(workflow)"
          >
            <div class="d-flex align-items-start justify-content-between gap-3 mb-2">
              <div class="d-flex align-items-start gap-3 min-w-0">
                <span class="workflow-template-icon d-inline-flex align-items-center justify-content-center">
                  <i class="bi" :class="workflow.icon || (workflow.highlighted ? 'bi-people' : workflow.type === 'Finance' ? 'bi-receipt' : workflow.type === 'Contract' ? 'bi-folder' : workflow.type === 'Security' ? 'bi-shield-lock' : 'bi-diagram-3')"></i>
                </span>
                <div class="min-w-0">
                  <div class="workflow-template-title">{{ workflow.name }}</div>
                  <div class="workflow-template-pills d-flex flex-wrap align-items-center gap-2 mt-2">
                    <span class="workflow-link-pill badge rounded-pill">
                      <i class="bi bi-file-earmark-text"></i>
                      <span>{{ workflow.policy }}</span>
                    </span>
                    <span class="workflow-link-pill badge rounded-pill">
                      <i class="bi bi-shield-check"></i>
                      <span>{{ workflow.control }}</span>
                    </span>
                  </div>
                </div>
              </div>

              <div class="d-flex flex-column align-items-end gap-2 flex-shrink-0">
                <span class="badge rounded-pill workflow-risk-pill" :class="workflow.risk.class">{{ workflow.risk.label }}</span>
                <span class="badge rounded-pill workflow-status-pill workflow-template-code-pill">{{ workflow.type_code }}</span>
              </div>
            </div>

            <div class="workflow-template-path text-truncate">
              <span v-for="(step, index) in workflow.stages" :key="step">
                <span>{{ step }}</span>
                <i v-if="index < workflow.stages.length - 1" class="bi bi-chevron-right mx-1"></i>
              </span>
            </div>

            <div class="workflow-running-footer mt-2">
              <span>{{ workflow.stage_count }} stages</span>
              <span>&middot;</span>
              <span>{{ workflow.connection_count }} connections</span>
              <span>&middot;</span>
              <span>{{ workflow.running_count }} running</span>
            </div>
          </article>
        </div>
      </div>
    </section>
  </div>

  <div
    v-if="active_drawer"
    class="command-centre-drawer-backdrop"
    @click="close_drawer"
  ></div>

  <aside
    v-if="active_drawer"
    class="command-centre-drawer"
    aria-label="Workflow detail drawer"
  >
    <div class="command-centre-drawer-header border-bottom">
      <div class="d-flex align-items-start justify-content-between gap-3">
        <div class="min-w-0">
          <div class="command-centre-drawer-kicker text-uppercase">{{ active_drawer.section }}</div>
          <h2 class="command-centre-drawer-title mb-1 text-truncate">{{ active_drawer.title }}</h2>
          <div class="command-centre-drawer-subtitle text-truncate">{{ active_drawer.subtitle }}</div>
        </div>
        <div class="d-flex align-items-center gap-2 flex-shrink-0">
          <span class="badge rounded-pill" :class="active_drawer.status_class">{{ active_drawer.status }}</span>
          <button
            type="button"
            class="btn btn-link text-decoration-none p-0 command-centre-drawer-close"
            @click="close_drawer"
            aria-label="Close drawer"
          >
            <i class="bi bi-x-lg fs-5"></i>
          </button>
        </div>
      </div>
    </div>

    <div ref="drawerBody" class="command-centre-drawer-body">
      <template v-if="active_drawer.details || active_drawer.sections">
        <div
          v-if="active_drawer.details_layout === 'list'"
          class="command-centre-drawer-details-list mb-4"
        >
          <div
            v-for="row in active_drawer.details"
            :key="row.label"
            class="command-centre-drawer-details-row d-flex align-items-start justify-content-between gap-3"
          >
            <div class="command-centre-drawer-meta-label text-nowrap">{{ row.label }}</div>
            <div class="command-centre-drawer-meta-value text-end" :class="row.value_class || ''">{{ row.value }}</div>
          </div>
        </div>

        <div v-else class="row g-3 mb-4">
          <div
            v-for="row in active_drawer.details"
            :key="row.label"
            class="col-12 col-md-6"
          >
            <div class="command-centre-drawer-meta">
              <div class="command-centre-drawer-meta-label">{{ row.label }}</div>
              <div class="command-centre-drawer-meta-value" :class="row.value_class || ''">{{ row.value }}</div>
            </div>
          </div>
        </div>

        <section
          v-for="section in active_drawer.sections"
          :key="section.title"
          class="mb-4"
        >
          <div class="d-flex align-items-start justify-content-between gap-2 mb-3">
            <div class="command-centre-drawer-section-title">{{ section.title }}</div>
            <span v-if="section.badge" class="badge rounded-pill" :class="section.badge_class">{{ section.badge }}</span>
          </div>

          <div v-if="section.kind === 'stage'" class="workflow-drawer-stage-card rounded-4 p-3">
            <div class="d-flex align-items-center gap-3">
              <span class="workflow-drawer-stage-icon d-inline-flex align-items-center justify-content-center">
                <i class="bi bi-box-seam"></i>
              </span>
              <div class="min-w-0">
                <div class="workflow-drawer-stage-kicker text-uppercase">Current stage</div>
                <div class="workflow-drawer-stage-title text-truncate">{{ section.current }}</div>
                <div class="workflow-drawer-stage-meta">{{ section.stage_meta }}</div>
              </div>
            </div>
          </div>

          <div v-else-if="section.kind === 'chips'" class="d-flex flex-wrap gap-2">
            <span
              v-for="chip in section.items"
              :key="chip"
              class="workflow-drawer-chip badge rounded-pill"
            >
              {{ chip }}
            </span>
          </div>

          <div v-if="section.kind === 'items'" class="d-flex flex-column gap-2">
            <article
              v-for="item in section.items"
              :key="item.title"
              class="command-centre-drawer-item d-flex align-items-start justify-content-between gap-3"
            >
              <div class="d-flex align-items-start gap-2 min-w-0">
                <span :class="['command-centre-drawer-item-icon d-inline-flex align-items-center justify-content-center', item.icon_class]">
                  <i class="bi" :class="item.icon"></i>
                </span>
                <div class="min-w-0">
                  <div class="command-centre-drawer-item-title">{{ item.title }}</div>
                  <div class="command-centre-drawer-item-subtitle">{{ item.subtitle }}</div>
                  <div class="command-centre-drawer-item-meta">{{ item.meta }}</div>
                </div>
              </div>
              <span
                v-if="item.badge"
                class="badge rounded-pill text-nowrap fw-semibold"
                :class="item.badge_class"
              >
                {{ item.badge }}
              </span>
            </article>
          </div>

          <div v-else-if="section.kind === 'timeline'" class="position-relative ps-1">
            <div class="command-centre-drawer-timeline">
              <article
                v-for="item in section.items"
                :key="item.title"
                :class="[
                  'command-centre-drawer-timeline-item',
                  item.connected ? 'is-connected' : ''
                ]"
              >
                <span :class="['command-centre-drawer-timeline-badge', item.icon_class]">
                  <i class="bi" :class="item.icon"></i>
                </span>
                <span v-if="item.connected" class="command-centre-drawer-timeline-line"></span>
                <div class="command-centre-drawer-timeline-content">
                  <div class="command-centre-drawer-item-title">{{ item.title }}</div>
                  <div v-if="item.subtitle" class="command-centre-drawer-item-subtitle">{{ item.subtitle }}</div>
                  <div v-if="item.meta" class="command-centre-drawer-timeline-meta">
                    <span>{{ item.meta }}</span>
                  </div>
                </div>
              </article>
            </div>
          </div>

          <div v-else-if="section.kind === 'progress'" class="d-flex flex-column gap-3">
            <div
              v-for="item in section.items"
              :key="item.label"
              class="command-centre-drawer-progress-row"
            >
              <div class="d-flex align-items-center justify-content-between gap-3 mb-2">
                <div class="d-flex align-items-center gap-2 min-w-0">
                  <span class="command-centre-drawer-dot rounded-circle flex-shrink-0" :style="{ backgroundColor: item.dot }"></span>
                  <div class="command-centre-drawer-item-title text-truncate">{{ item.label }}</div>
                </div>
                <div class="d-flex align-items-center gap-3 flex-shrink-0">
                  <span class="command-centre-drawer-progress-value">{{ item.percent }}%</span>
                  <span class="command-centre-drawer-progress-count">{{ item.count }}</span>
                </div>
              </div>
              <div class="progress rounded-pill command-centre-drawer-progress">
                <div class="progress-bar bg-success" :style="{ width: `${item.percent}%` }"></div>
              </div>
            </div>
          </div>
        </section>
      </template>

      <template v-else>
        <div class="h-100 d-flex align-items-center justify-content-center text-center px-3">
          <div>
            <div class="d-inline-flex align-items-center justify-content-center rounded-circle bg-light text-secondary mb-3" style="width: 3rem; height: 3rem;">
              <i class="bi bi-info-circle"></i>
            </div>
            <div class="fw-semibold text-dark mb-1">{{ active_drawer.body_title }}</div>
            <div class="small text-secondary">
              {{ active_drawer.body_copy }}
            </div>
          </div>
        </div>
      </template>
    </div>

    <div v-if="active_drawer.footer_actions && active_drawer.footer_actions.length" class="command-centre-drawer-footer border-top">
      <div class="d-flex flex-wrap gap-2 align-items-center">
        <div
          v-if="active_drawer_footer_primary_actions.length"
          class="command-centre-drawer-footer-left d-flex align-items-center gap-2"
        >
          <button
            v-for="action in active_drawer_footer_primary_actions"
            :key="action.label"
            type="button"
            class="btn d-inline-flex align-items-center gap-2"
            :class="action.variant === 'primary'
              ? 'config-navbar-primary-action'
              : action.variant === 'secondary'
                ? 'config-navbar-secondary-action'
                : action.variant === 'outline-danger'
                  ? 'btn-outline-danger'
                  : action.variant === 'outline-warning'
                    ? 'btn-outline-warning'
                    : action.variant === 'outline-secondary'
                      ? 'btn-outline-secondary'
                      : 'btn-link text-decoration-none text-secondary px-0'"
          >
            <i class="bi" :class="action.icon"></i>
            <span>{{ action.label }}</span>
          </button>
        </div>

        <div class="command-centre-drawer-footer-actions ms-auto d-flex flex-wrap gap-2 align-items-center">
          <button
            v-for="action in active_drawer_footer_actions"
            :key="action.label"
            type="button"
            class="btn d-inline-flex align-items-center gap-2"
            :class="action.variant === 'primary'
              ? 'config-navbar-primary-action'
              : action.variant === 'secondary'
                ? 'config-navbar-secondary-action'
                : action.variant === 'outline-danger'
                  ? 'btn-outline-danger'
                  : action.variant === 'outline-warning'
                    ? 'btn-outline-warning'
                    : action.variant === 'outline-secondary'
                      ? 'btn-outline-secondary'
                      : 'btn-link text-decoration-none text-secondary px-0'"
          >
            <i class="bi" :class="action.icon"></i>
            <span>{{ action.label }}</span>
          </button>
        </div>
      </div>
    </div>
  </aside>

  <teleport to="body">
    <div
      v-if="show_start_workflow_modal"
      class="new-workflow-modal-backdrop"
      @click="close_start_workflow_modal"
    >
      <div class="new-workflow-modal-dialog" @click.stop>
        <div class="card new-workflow-modal-content rounded-4 overflow-hidden">
          <div class="new-workflow-modal-header">
            <div class="d-flex align-items-start justify-content-between gap-3">
              <div class="min-w-0">
                <h2 class="new-workflow-modal-title mb-2">Start a workflow</h2>
                <p class="new-workflow-modal-subtitle mb-0">
                  Launch a new instance from one of the configured process templates.
                </p>
              </div>
              <button
                type="button"
                class="btn btn-link text-decoration-none p-0 new-workflow-modal-close"
                @click="close_start_workflow_modal"
                aria-label="Close modal"
              >
                <i class="bi bi-x-lg fs-4"></i>
              </button>
            </div>
          </div>

          <div class="new-workflow-modal-scroll">
            <div class="new-workflow-modal-body">
              <div class="row g-3">
                <div class="col-12">
                  <label class="new-workflow-modal-field-label">TEMPLATE</label>
                  <select v-model="start_workflow_form.template_id" class="form-select new-workflow-modal-select">
                    <option v-for="template in start_workflow_templates" :key="template.id" :value="template.id">
                      {{ template.label }}
                    </option>
                  </select>
                </div>
                <div class="col-12">
                  <label class="new-workflow-modal-field-label">WORKFLOW NAME</label>
                  <input
                    v-model="start_workflow_form.workflow_name"
                    type="text"
                    class="form-control new-workflow-modal-input"
                    placeholder="e.g. Acme Corp onboarding"
                  >
                </div>
                <div class="col-12">
                  <label class="new-workflow-modal-field-label">OWNER</label>
                  <input
                    v-model="start_workflow_form.owner"
                    type="text"
                    class="form-control new-workflow-modal-input"
                    placeholder="Responsible person"
                  >
                </div>
                <div class="col-12">
                  <div class="new-workflow-modal-summary rounded-4 p-3">
                    <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                      <span class="badge rounded-pill" :class="active_start_workflow_template.risk.class">
                        {{ active_start_workflow_template.risk.label }}
                      </span>
                      <span class="workflow-link-pill badge rounded-pill">
                        <i class="bi bi-file-earmark-text"></i>
                        <span>{{ active_start_workflow_template.policy }}</span>
                      </span>
                    </div>
                    <div class="workflow-link-pill badge rounded-pill mb-2">
                      <i class="bi bi-shield-check"></i>
                      <span>{{ active_start_workflow_template.control }}</span>
                    </div>
                    <div class="new-workflow-modal-summary-meta">
                      {{ active_start_workflow_template.note }}
                    </div>
                    <div class="new-workflow-modal-summary-footer">
                      {{ active_start_workflow_template.stage_count || 6 }} stages
                      <span>&middot;</span>
                      {{ active_start_workflow_template.connection_count || 6 }} module connections
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="new-workflow-modal-note rounded-4 p-3 d-flex align-items-start gap-2">
                    <i class="bi bi-info-circle"></i>
                    <span>
                      This is an OnboardOS journey - the instance runs in OnboardOS and hands the activated record back to Complyon on completion.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="new-workflow-modal-footer d-flex align-items-center justify-content-end gap-2">
            <button type="button" class="btn btn-link text-decoration-none text-secondary px-0" @click="close_start_workflow_modal">Cancel</button>
            <button type="button" class="btn btn-primary-dark d-inline-flex align-items-center gap-2 px-3 py-2 rounded-3 fw-semibold" @click="start_workflow">
              <i class="bi bi-plus-lg"></i>
              <span>Start workflow</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</div>
